import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import AppLayout from "@/layouts/AppLayout/AppLayout";
import AuthLayout from "@/layouts/AuthLayout/AuthLayout";
import UserManagementPage from "@/pages/admin/UserManagementPage/UserManagementPage";
import LoginForm from "@/pages/auth/LoginPage/LoginForm";
import NewPasswordForm from "@/pages/auth/NewPasswordPage/NewPasswordForm";
import ResetPasswordForm from "@/pages/auth/ResetPasswordPage/ResetPasswordForm";
import CampaignOverviewLayout from "@/pages/CampaignOverviewPage/page/CampaignOverviewLayout";
import CampaignOverviewPage from "@/pages/CampaignOverviewPage/page/CampaignOverviewPage";
import { AuthStore$ } from "@/stores/authStore";
import { isGlobalAdmin } from "@/stores/userStore";
import { ROUTES } from "@/constants/routes";

const rootRoute = createRootRoute();

const authLayoutRoute = createRoute({
  component: AuthLayout,
  getParentRoute: () => rootRoute,
  id: "auth-layout",
});

const loginRoute = createRoute({
  component: LoginForm,
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.LOGIN,
});

const resetPasswordRoute = createRoute({
  component: ResetPasswordForm,
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.RESET_PASSWORD,
});

const newPasswordRoute = createRoute({
  component: NewPasswordForm,
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.NEW_PASSWORD,
  // The invite email carries the token here. Declaring it makes a missing one
  // an empty string the page can refuse, rather than `undefined` reaching a
  // request.
  validateSearch: (search: Record<string, unknown>): { token: string } => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

const protectedRoute = createRoute({
  beforeLoad: () => {
    if (!AuthStore$.isAuthenticated.get()) {
      throw redirect({ to: ROUTES.LOGIN });
    }
  },
  component: AppLayout,
  getParentRoute: () => rootRoute,
  id: "protected-layout",
});

const campaignOverviewLayoutRoute = createRoute({
  component: CampaignOverviewLayout,
  getParentRoute: () => protectedRoute,
  id: "campaign-overview-layout",
});

const campaignOverviewRoute = createRoute({
  component: CampaignOverviewPage,
  getParentRoute: () => campaignOverviewLayoutRoute,
  path: ROUTES.CAMPAIGN_OVERVIEW,
});

const userManagementRoute = createRoute({
  beforeLoad: () => {
    if (!isGlobalAdmin()) {
      throw redirect({ to: ROUTES.CAMPAIGN_OVERVIEW });
    }
  },
  component: UserManagementPage,
  getParentRoute: () => protectedRoute,
  path: ROUTES.ADMIN_USERS,
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([
    loginRoute,
    resetPasswordRoute,
    newPasswordRoute,
  ]),
  protectedRoute.addChildren([
    campaignOverviewLayoutRoute.addChildren([campaignOverviewRoute]),
    userManagementRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
